import React, { useEffect, useState } from "react";
import { FaWallet } from "react-icons/fa";
import Button from "../../components/common/button";
import { useDispatch, useSelector } from "react-redux";
import { fetchWalletData } from "../../redux/slices/walletSlice";
import { sendWithdrawRequest } from "../../redux/slices/withdrawSlice";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import BackdropSpinner from "../../components/backdropSpinner";

const Withdraw = () => {
  const { t } = useTranslation();
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isConfirmed, setIsConfirmed] = useState(false);

  const { balance,loading } = useSelector((state) => state.wallet); // Redux state for wallet balance
  const { loadingSend} = useSelector((state) => state.withdraw); // Redux state for withdrawal
  const dispatch = useDispatch();
  const userId = localStorage.getItem("userID");

  useEffect(() => {
    if (userId) {
      dispatch(fetchWalletData(userId)); // Fetch wallet data on component mount
    }
  }, [userId, dispatch]);

  const handleWithdraw = async () => {
    const amount = parseInt(withdrawAmount, 10);
  
    if (amount < 100) {
      setErrorMessage(t("withdraw.minimumAmountError", { minAmount: 100 }));
      return;
    }
  
    if (amount > 0 && amount <= balance) {
      const requestData = {
        user_id: userId,
        withdraw_amount: amount,
      };
  
      try {
        await dispatch(sendWithdrawRequest(requestData)).unwrap();
        toast.success(t("withdraw.withdrawRequestSuccess"));
        setErrorMessage("");
        setWithdrawAmount("");
        setIsConfirmed(false);
      } catch (error) {
        console.error("Withdraw Error:", error);
        if(error?.message === "You already have a withdrawal request in progress."){
        toast.error(t("withdraw.withdrawal_request_in_progress") || "An error occurred. Please try again.");}
      }
    } else {
      setErrorMessage(
        `${t("withdraw.validAmount", { maxWithdrawAmount: balance })}`
      );
    }
  };
  
  const handleInputChange = (e) => {
    const value = parseInt(e.target.value, 10);
  
    if (!isNaN(value)) {
      setWithdrawAmount(value);
      if (value < 100) {
        setErrorMessage(t("withdraw.minimumAmountError", { minAmount: 100 }));
      } else {
        setErrorMessage("");
      }
    } else {
      setWithdrawAmount("");
      setErrorMessage(t("withdraw.validNumber"));
    }
  };
  

  if (loading) {
    return <BackdropSpinner />;
  }


  return (
    <>
    {/* Title */}
    <div className="flex justify-center my-2">
        <p className="text-white text-lg sm:text-xl md:text-2xl font-medium text-center">
        {t("withdraw.withdrawFunds")}
        </p>
      </div>

      {/* Main Content */}
      <div className="flex justify-center items-center text-white">
        <div className="p-6 w-full max-w-lg sm:max-w-md rounded-lg shadow-md">
          {/* Wallet Info */}
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-blue-500 via-indigo-600 to-purple-700 rounded-full border-4 border-[#BB9A3E] shadow-md text-white text-2xl">
              <FaWallet size={40} />
            </div>
          </div>

          {/* Current Balance */}
          <div className="rounded-lg p-4 shadow-inner mb-6 bg-gray-800">
            <h3 className="text-sm font-semibold uppercase">
              {t("withdraw.currentBalance")}
            </h3>
            <p className="text-2xl font-bold">₹{balance}</p>
          </div>

          {/* Withdrawal Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              {t("withdraw.withdrawAmount")}
            </label>
            <input
              type="number"
              value={withdrawAmount}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-500 rounded-md text-sm bg-transparent text-white focus:outline-none focus:border-[#BB9A3E]"
              placeholder={t("withdraw.enterAmount", {
                maxWithdrawAmount: balance,
              })}
            />
            {errorMessage && (
              <p className="text-red-500 text-sm mt-2">{errorMessage}</p>
            )}
          </div>

          {/* Confirmation Checkbox */}
          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={isConfirmed}
                onChange={(e) => setIsConfirmed(e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm">{t("withdraw.confirm")}</span>
            </label>
          </div>

          {/* Withdraw Button */}
          <div className="flex justify-center mb-4">
            <Button
              onClick={handleWithdraw}
              disabled={
                !isConfirmed ||
                withdrawAmount <= 99 ||
                withdrawAmount > balance ||
                loadingSend
              }
              text={loadingSend ? t("loading") : t("withdraw.withdraw")}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Withdraw;
